import indexedDbService from '@/services/indexedDbService'
import encryptionService from '@/services/encryptionService'

const DRAFT_NAMESPACE = 'messages_drafts'
const LEGACY_DB_NAME = 'tp'
const LEGACY_STORE_NAME = 'cachedReplies'
const MIGRATION_FLAG_PREFIX = 'tp_drafts_migrated'
const DRAFT_OWNER_KEY = 'tp_drafts_owner_userId'

export interface DraftEntry {
  threadId: number
  userId: number
  message: string
  messagePreview: string
}

interface LegacyCachedReply {
  threadId: number
  userId: number
  message: ArrayBuffer
  messagePreview: ArrayBuffer
  iv: Uint8Array
  previewIv: Uint8Array
  salt: string
  attachments?: File[]
}

function draftKey(threadId: number): string {
  return `thread_${threadId}`
}

export async function saveDraft(draft: DraftEntry): Promise<void> {
  try {
    await indexedDbService.set(draftKey(draft.threadId), draft, {
      namespace: DRAFT_NAMESPACE,
      encrypt: true
    })
    setLastDraftOwnerUserId(draft.userId)
  } catch (err) {
    console.warn('[MessagesDraft] Failed to save draft:', err)
  }
}

export async function getDraft(threadId: number): Promise<DraftEntry | null> {
  try {
    return await indexedDbService.get<DraftEntry>(draftKey(threadId), {
      namespace: DRAFT_NAMESPACE,
      decrypt: true
    })
  } catch (err) {
    console.warn('[MessagesDraft] Failed to load draft:', err)
    return null
  }
}

export async function deleteDraft(threadId: number): Promise<void> {
  try {
    await indexedDbService.delete(draftKey(threadId), {
      namespace: DRAFT_NAMESPACE
    })
  } catch (err) {
    console.warn('[MessagesDraft] Failed to delete draft:', err)
  }
}

export async function getAllDraftPreviews(): Promise<DraftEntry[]> {
  try {
    const result = await indexedDbService.query<DraftEntry>({
      namespace: DRAFT_NAMESPACE
    })

    return result.entries
  } catch (err) {
    console.warn('[MessagesDraft] Failed to load draft previews:', err)
    return []
  }
}

export async function deleteAllDrafts(): Promise<void> {
  try {
    await indexedDbService.clearNamespace(DRAFT_NAMESPACE)
    localStorage.removeItem(DRAFT_OWNER_KEY)
  } catch (err) {
    console.warn('[MessagesDraft] Failed to delete all drafts:', err)
  }
}

export async function hasDraftsForCurrentUser(
  userId: number
): Promise<boolean> {
  const drafts = await getAllDraftPreviews()

  return drafts.some((d) => d.userId === userId)
}

export async function hasAnyDrafts(): Promise<boolean> {
  const drafts = await getAllDraftPreviews()

  return drafts.length > 0
}

export function getLastDraftOwnerUserId(): number | null {
  const stored = localStorage.getItem(DRAFT_OWNER_KEY)
  if (!stored) return null

  const parsed = +stored
  return Number.isFinite(parsed) ? parsed : null
}

export function setLastDraftOwnerUserId(userId: number): void {
  localStorage.setItem(DRAFT_OWNER_KEY, String(userId))
}

export function isDifferentDraftOwner(currentUserId: number): boolean {
  const lastOwner = getLastDraftOwnerUserId()
  return lastOwner !== null && lastOwner !== currentUserId
}

export async function purgeDraftsIfDifferentUser(
  currentUserId: number
): Promise<void> {
  if (isDifferentDraftOwner(currentUserId)) {
    await deleteAllDrafts()
  }
  setLastDraftOwnerUserId(currentUserId)
}

/**
 * Run migration, purge stale drafts, and return drafts belonging to the
 * given user. Intended as the single entry-point for draft bootstrap so
 * callers don't need to orchestrate the three steps individually.
 */
export async function initializeDraftsForUser(
  caseId: number,
  userId: number
): Promise<DraftEntry[]> {
  await migrateLegacyDrafts(caseId, userId)
  await purgeDraftsIfDifferentUser(userId)

  const drafts = await getAllDraftPreviews()
  return drafts.filter((d) => d.userId === userId)
}

function migrationKey(userId: number): string {
  return `${MIGRATION_FLAG_PREFIX}_${userId}`
}

function openLegacyDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(LEGACY_DB_NAME)

      request.onerror = () => resolve(null)

      request.onsuccess = () => {
        const db = request.result

        if (!db.objectStoreNames.contains(LEGACY_STORE_NAME)) {
          db.close()
          resolve(null)
          return
        }

        resolve(db)
      }

      // If upgrade fires, the DB didn't exist before - abort to avoid creating it
      request.onupgradeneeded = (event) => {
        const target = event.target as IDBOpenDBRequest
        try {
          target.transaction?.abort()
        } catch {
          // best-effort abort
        }
        resolve(null)
      }
    } catch {
      resolve(null)
    }
  })
}

function readAllLegacyRecords(db: IDBDatabase): Promise<LegacyCachedReply[]> {
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([LEGACY_STORE_NAME], 'readonly')
      const store = transaction.objectStore(LEGACY_STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result ?? [])
      request.onerror = () => reject(request.error)
    } catch (err) {
      reject(err)
    }
  })
}

function clearLegacyStore(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([LEGACY_STORE_NAME], 'readwrite')
      const store = transaction.objectStore(LEGACY_STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    } catch (err) {
      reject(err)
    }
  })
}

async function decryptLegacyRecord(
  record: LegacyCachedReply,
  indexedDBKey: string
): Promise<DraftEntry | null> {
  try {
    const key = await encryptionService.generateKeyFromData(
      indexedDBKey,
      record.salt
    )

    const decryptedMessage = await encryptionService.decryptData(
      key,
      record.iv,
      record.message
    )

    let decryptedPreview = ''
    try {
      decryptedPreview = await encryptionService.decryptData(
        key,
        record.previewIv,
        record.messagePreview
      )
    } catch {
      // Preview decryption failed - derive from message instead
      decryptedPreview = decryptedMessage.slice(0, 250)
    }

    return {
      threadId: record.threadId,
      userId: record.userId,
      message: decryptedMessage,
      messagePreview: decryptedPreview
    }
  } catch {
    console.warn(
      `[MessagesDraft] Failed to decrypt legacy draft for thread ${record.threadId}`
    )
    return null
  }
}

/**
 * One-time migration of legacy message drafts from the old IndexedDB schema
 * (DB: "tp", store: "cachedReplies") to the new indexedDbService storage
 * (DB: "TalkingParentsApp", namespace: "messages_drafts").
 *
 * Legacy records are encrypted per-record with AES-GCM. The decryption key
 * is derived from `${caseId}_${userId}` and a per-record salt.
 *
 * The migration is idempotent - a localStorage flag prevents re-runs.
 * Old data is cleared from the legacy store after a fully successful migration
 * (zero failures). If any record fails to migrate, old data is kept so a
 * retry can re-attempt on next load.
 */
export async function migrateLegacyDrafts(
  caseId: number,
  userId: number
): Promise<{ migrated: number; failed: number }> {
  const flag = migrationKey(userId)

  if (localStorage.getItem(flag) === 'done') {
    return { migrated: 0, failed: 0 }
  }

  let migrated = 0
  let failed = 0

  try {
    const db = await openLegacyDb()

    if (!db) {
      // No legacy DB or no cachedReplies store - nothing to migrate
      localStorage.setItem(flag, 'done')
      return { migrated: 0, failed: 0 }
    }

    let records: LegacyCachedReply[]
    try {
      records = await readAllLegacyRecords(db)
    } finally {
      db.close()
    }

    if (!records.length) {
      localStorage.setItem(flag, 'done')
      return { migrated: 0, failed: 0 }
    }

    const indexedDBKey = `${caseId}_${userId}`

    for (const record of records) {
      const draft = await decryptLegacyRecord(record, indexedDBKey)

      if (!draft) {
        failed++
        continue
      }

      // Skip empty drafts
      if (!draft.message.trim()) {
        continue
      }

      try {
        await saveDraft(draft)
        migrated++
      } catch {
        failed++
      }
    }

    if (failed === 0) {
      localStorage.setItem(flag, 'done')

      // All records migrated (or were empty) - clean up legacy store
      try {
        const cleanupDb = await openLegacyDb()
        if (cleanupDb) {
          try {
            await clearLegacyStore(cleanupDb)
          } finally {
            cleanupDb.close()
          }
        }
      } catch {
        // Cleanup is best-effort; migration itself succeeded
        console.warn(
          '[MessagesDraft] Could not clear legacy store after migration'
        )
      }
    }

    if (migrated > 0) {
      console.info(
        `[MessagesDraft] Migrated ${migrated} legacy draft(s) to new storage`
      )
    }
    if (failed > 0) {
      console.warn(
        `[MessagesDraft] Failed to migrate ${failed} legacy draft(s) - legacy data preserved for retry`
      )
    }
  } catch (err) {
    console.warn('[MessagesDraft] Legacy draft migration failed:', err)
    // Do NOT set the flag - allow retry on next load
  }

  return { migrated, failed }
}
